import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import Header from '../../components/Header';
import PlanCard from '../../components/planes/PlanCard';
import TablaComparativa from '../../components/planes/TablaComparativa';
import { NOTAS_GENERALES, PLANES, TYC_URL, TYC_VERSION } from '../../data/planes';
import {
  Card,
  Segmented,
  SegmentedOption,
  IconSwap,
  cn,
  staggerParent,
  enterChild,
  easeStandard,
} from '../../components/ui';
import { ChevronDownIcon, ChevronUpIcon, ExternalLinkIcon, InfoIcon } from '../../components/icons';

type Vista = 'tarjetas' | 'tabla';

const VISTAS: SegmentedOption<Vista>[] = [
  { value: 'tarjetas', label: 'Tarjetas', title: 'Un plan por tarjeta, con el detalle completo' },
  { value: 'tabla', label: 'Tabla comparativa', title: 'Todas las prestaciones lado a lado' },
];

/** Cambio de vista: alta frecuencia, así que entra y sale corto. */
const vistaSwitch = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: { duration: 0.2, ease: 'easeOut' as const },
};

const PlanesPage: React.FC = () => {
  const [vista, setVista] = useState<Vista>('tarjetas');
  const [notasAbiertas, setNotasAbiertas] = useState(true);

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <Header />

      <main className="container mx-auto flex-grow px-4 py-8">
        <motion.div
          variants={staggerParent}
          initial="hidden"
          animate="visible"
          className="mx-auto max-w-7xl space-y-6"
        >
          <motion.div
            variants={enterChild}
            className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between"
          >
            <div className="min-w-0">
              <h1 className="font-poppins text-2xl font-semibold text-gappex-secondary">
                Planes vigentes
              </h1>
              <p className="mt-1 max-w-2xl text-sm text-slate-500">
                Los planes vigentes según los Términos y Condiciones de {TYC_VERSION}. Algunos pueden
                no aparecer en la app para contratar: no todos se comercializan por ese canal.
              </p>
            </div>

            <a
              href={TYC_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                'inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-white py-2 ps-3.5 pe-3',
                'text-sm font-semibold text-slate-600 shadow-elevate',
                'transition-[color,box-shadow] duration-150 ease-out',
                'hover:text-slate-900 hover:shadow-elevate-hover',
                'outline-none focus-visible:ring-2 focus-visible:ring-gappex-primary/40',
              )}
            >
              Ver Términos y Condiciones
              <ExternalLinkIcon />
            </a>
          </motion.div>

          <motion.div variants={enterChild}>
            <Segmented
              options={VISTAS}
              value={vista}
              onChange={setVista}
              ariaLabel="Cambiar vista de planes"
            />
          </motion.div>

          <motion.div variants={enterChild}>
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={vista}
                initial={vistaSwitch.initial}
                animate={vistaSwitch.animate}
                exit={vistaSwitch.exit}
                transition={vistaSwitch.transition}
              >
                {vista === 'tarjetas' ? (
                  <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                    {PLANES.map((plan) => (
                      <PlanCard key={plan.id} plan={plan} />
                    ))}
                  </div>
                ) : (
                  <TablaComparativa planes={PLANES} />
                )}
              </motion.div>
            </AnimatePresence>
          </motion.div>

          <motion.div variants={enterChild}>
            <Card as="section" className="p-5">
              <button
                type="button"
                onClick={() => setNotasAbiertas((v) => !v)}
                aria-expanded={notasAbiertas}
                aria-controls="notas-generales-panel"
                className="flex w-full items-center justify-between gap-3 rounded-lg text-left outline-none focus-visible:ring-2 focus-visible:ring-gappex-primary/40"
              >
                <h2 className="font-poppins text-base font-semibold text-gappex-secondary">
                  Aclaraciones generales
                </h2>
                <span className="text-slate-400">
                  <IconSwap swapKey={notasAbiertas ? 'up' : 'down'}>
                    {notasAbiertas ? <ChevronUpIcon /> : <ChevronDownIcon />}
                  </IconSwap>
                </span>
              </button>

              <AnimatePresence initial={false}>
                {notasAbiertas && (
                  <motion.ul
                    id="notas-generales-panel"
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0, transition: { duration: 0.25, ease: easeStandard } }}
                    exit={{ opacity: 0, y: -8, transition: { duration: 0.15, ease: 'easeOut' } }}
                    className="mt-4 list-disc space-y-2 ps-5"
                  >
                    {NOTAS_GENERALES.map((nota) => (
                      <li key={nota} className="text-sm text-slate-600">
                        {nota}
                      </li>
                    ))}
                  </motion.ul>
                )}
              </AnimatePresence>
            </Card>
          </motion.div>

          {/* Nota legal permanente: no es un aviso que aparece y desaparece, así
              que no usa Alert (no debe anunciarse como `status` al lector). */}
          <motion.div
            variants={enterChild}
            className="flex items-start gap-3 rounded-xl border-l-4 border-sky-500 bg-sky-50 p-4 shadow-elevate"
          >
            <span className="mt-px shrink-0 text-sky-500">
              <InfoIcon strokeWidth={2} />
            </span>
            <p className="text-xs text-sky-800">
              Ante cualquier diferencia, prevalecen los Términos y Condiciones publicados en
              gappex.com.ar.{' '}
              <a
                href={TYC_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded font-semibold underline decoration-sky-300 underline-offset-2 transition-colors duration-150 ease-out hover:text-sky-950 hover:decoration-sky-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
              >
                Ver Términos y Condiciones
              </a>
            </p>
          </motion.div>
        </motion.div>
      </main>

      <footer className="mt-auto bg-gappex-dark py-6 text-white">
        <div className="container mx-auto px-4 text-center">
          <p className="mb-0 text-sm">
            © {new Date().getFullYear()} Gappex — Validación de Suscriptores
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PlanesPage;
